/**
 * Currency Formatting Utilities
 * Format numbers as Tanzanian Shillings
 */
import { CURRENCY } from '../constants/systemConfig';

/**
 * Format number as currency
 * @param {number} amount - Amount to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    symbol = CURRENCY.SYMBOL,
    decimalPlaces = CURRENCY.DECIMAL_PLACES,
    showSymbol = true,
    showDecimals = true,
  } = options;

  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? `${symbol} 0` : '0';
  }

  const numAmount = Number(amount);
  const decimals = showDecimals ? decimalPlaces : 0;

  const formatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return showSymbol ? `${symbol} ${formatted}` : formatted;
};

/**
 * Format number as compact currency (K, M, B)
 * @param {number} amount - Amount to format
 * @returns {string} Compact formatted currency
 */
export const formatCompactCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${CURRENCY.SYMBOL} 0`;
  }

  const numAmount = Number(amount);
  const absAmount = Math.abs(numAmount);

  if (absAmount >= 1e9) {
    return `${CURRENCY.SYMBOL} ${(numAmount / 1e9).toFixed(1)}B`;
  }
  if (absAmount >= 1e6) {
    return `${CURRENCY.SYMBOL} ${(numAmount / 1e6).toFixed(1)}M`;
  }
  if (absAmount >= 1e3) {
    return `${CURRENCY.SYMBOL} ${(numAmount / 1e3).toFixed(1)}K`;
  }

  return formatCurrency(numAmount);
};

/**
 * Parse currency string to number
 * @param {string} value - Currency string
 * @returns {number} Parsed number
 */
export const parseCurrency = (value) => {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const cleaned = value
    .replace(CURRENCY.SYMBOL, '')
    .replace(/[^\d.-]/g, '');

  return parseFloat(cleaned) || 0;
};

/**
 * Calculate loan EMI
 * @param {number} principal - Loan amount
 * @param {number} rate - Annual interest rate (percentage)
 * @param {number} tenure - Loan tenure in months
 * @returns {number} Monthly EMI
 */
export const calculateEMI = (principal, rate, tenure) => {
  if (!principal || !rate || !tenure) return 0;

  const monthlyRate = rate / 100 / 12;
  const emi =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
    (Math.pow(1 + monthlyRate, tenure) - 1);

  return Math.round(emi * 100) / 100;
};

/**
 * Calculate total interest payable
 * @param {number} principal - Loan amount
 * @param {number} emi - Monthly EMI
 * @param {number} tenure - Loan tenure in months
 * @returns {number} Total interest
 */
export const calculateTotalInterest = (principal, emi, tenure) => {
  return Math.round((emi * tenure - principal) * 100) / 100;
};

/**
 * Calculate reducing balance interest
 * @param {number} principal - Remaining principal
 * @param {number} rate - Annual interest rate (percentage)
 * @returns {number} Monthly interest
 */
export const calculateReducingBalanceInterest = (principal, rate) => {
  return Math.round((principal * rate / 100 / 12) * 100) / 100;
};

export default {
  formatCurrency,
  formatCompactCurrency,
  parseCurrency,
  calculateEMI,
  calculateTotalInterest,
  calculateReducingBalanceInterest,
};
